import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "@hooks/useAuth";

import legacyMigrationService, {
  LegacyMigrationError,
  LegacyMigrationExtraction,
  LegacyMigrationOutcome,
  LegacyMigrationStatus,
  LegacyMigrationTenant,
  toLegacyMigrationError
} from "./service";

/** Users per request: a batch answers well inside any gateway's patience. */
const BATCH_SIZE = 20;

/** Lock outcomes that leave the Topcon Legado login locked; any other code is a lock that did not happen. */
const LOCKED_REASONS = new Set(["LEGACY_LOCKED", "LEGACY_ALREADY_LOCKED"]);

/** Where the operator is. Local state: nothing about the run is kept on the server. */
export type TStep = "customer" | "reading" | "review" | "migrating" | "done";

export interface MigrationProgress {
  done: number;
  total: number;
  created: number;
  updated: number;
  skipped: number;
  errors: number;
  passwordsPreserved: number;
  resetEmailsSent: number;
  /** Topcon Legado passwords locked by this run. */
  legacyLocked: number;
  /** Users flagged for the lock whose password could not be locked. */
  legacyLockFailed: number;
}

const EMPTY_PROGRESS: MigrationProgress = {
  done: 0,
  total: 0,
  created: 0,
  updated: 0,
  skipped: 0,
  errors: 0,
  passwordsPreserved: 0,
  resetEmailsSent: 0,
  legacyLocked: 0,
  legacyLockFailed: 0
};

/** Adds (or removes) these ids to a set, returning a new one. */
const withIds = (current: Set<string>, legacyIds: string[], add: boolean): Set<string> => {
  const next = new Set(current);

  legacyIds.forEach((legacyId) => {
    if (add) next.add(legacyId);
    else next.delete(legacyId);
  });

  return next;
};

export interface LegacyMigrationState {
  step: TStep;
  status: LegacyMigrationStatus | null;
  tenants: LegacyMigrationTenant[];
  tenantsLoaded: boolean;
  extraction: LegacyMigrationExtraction | null;
  /** Legacy ids the operator wants to migrate. */
  selected: Set<string>;
  /** Legacy ids whose Topcon Legado password will be locked. */
  lockLegacy: Set<string>;
  progress: MigrationProgress;
  outcomes: LegacyMigrationOutcome[];
  stopping: boolean;
  loading: boolean;
  busy: boolean;
  error: LegacyMigrationError | null;
  startExtraction: (tenantId: string) => Promise<void>;
  toggle: (legacyId: string, selected: boolean) => void;
  /** Selects or unselects these users — the ones the review is showing — and nobody else. */
  toggleMany: (legacyIds: string[], selected: boolean) => void;
  toggleLock: (legacyId: string, lock: boolean) => void;
  /** Flags or unflags these users for the Topcon Legado lock, and nobody else. */
  toggleLockMany: (legacyIds: string[], lock: boolean) => void;
  migrate: () => Promise<void>;
  stop: () => void;
  leave: () => void;
}

/**
 * State of the legacy migration assistant.
 *
 * The migration runs here, in the browser: one extraction, then one request per
 * batch until the reviewed list is done. Closing the screen stops it, and
 * running a customer again is safe — provisioning matches users by e-mail, and
 * whoever is already in Identity starts unselected.
 */
export const useLegacyMigration = (): LegacyMigrationState => {

  const { getToken } = useAuth();

  const service = useMemo(() => legacyMigrationService(getToken), [getToken]);

  const [step, setStep] = useState<TStep>("customer");
  const [status, setStatus] = useState<LegacyMigrationStatus | null>(null);
  const [tenants, setTenants] = useState<LegacyMigrationTenant[]>([]);
  const [tenantsLoaded, setTenantsLoaded] = useState<boolean>(false);
  const [extraction, setExtraction] = useState<LegacyMigrationExtraction | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [lockLegacy, setLockLegacy] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState<MigrationProgress>(EMPTY_PROGRESS);
  const [outcomes, setOutcomes] = useState<LegacyMigrationOutcome[]>([]);
  const [stopping, setStopping] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<LegacyMigrationError | null>(null);

  const mounted = useRef<boolean>(true);
  /** Read inside the batch loop, which a state value would not see change. */
  const stopRequested = useRef<boolean>(false);

  useEffect(() => () => { mounted.current = false; }, []);

  const startExtraction = useCallback(async (tenantId: string) => {
    setStep("reading");
    setExtraction(null);
    setOutcomes([]);
    setProgress(EMPTY_PROGRESS);
    setBusy(true);
    setError(null);

    try {
      const result = await service.extract(tenantId);

      if (!mounted.current) return;

      const eligible = result.candidates.filter((candidate) => candidate.eligible);

      setExtraction(result);

      // Whoever is already in Identity starts unselected: this is what makes
      // running a customer again pick up only what is left.
      setSelected(new Set(eligible.filter((candidate) => !candidate.inIdentity).map((candidate) => candidate.legacyId)));

      // Locked by default only for whoever keeps the legacy password and uses
      // nothing else of the Topcon Legado: anyone with other legacy areas probably
      // still needs it, and anyone without the password kept would be left out of both.
      setLockLegacy(new Set(eligible
        .filter((candidate) => !candidate.legacyLocked && candidate.passwordKept && candidate.otherLegacyAreas.length === 0)
        .map((candidate) => candidate.legacyId)));

      setStep("review");
    } catch (rejection) {
      if (!mounted.current) return;

      // The banner says what went wrong; going back to the list is all the operator can do.
      setError(toLegacyMigrationError(rejection));
      setStep("customer");
    } finally {
      if (mounted.current) setBusy(false);
    }
  }, [service]);

  const toggle = useCallback((legacyId: string, isSelected: boolean) => {
    setSelected((current) => withIds(current, [legacyId], isSelected));
  }, []);

  const toggleMany = useCallback((legacyIds: string[], isSelected: boolean) => {
    setSelected((current) => withIds(current, legacyIds, isSelected));
  }, []);

  const toggleLock = useCallback((legacyId: string, lock: boolean) => {
    setLockLegacy((current) => withIds(current, [legacyId], lock));
  }, []);

  const toggleLockMany = useCallback((legacyIds: string[], lock: boolean) => {
    setLockLegacy((current) => withIds(current, legacyIds, lock));
  }, []);

  const migrate = useCallback(async () => {
    if (!extraction) return;

    const queue = extraction.candidates.filter((candidate) => candidate.eligible && selected.has(candidate.legacyId));

    if (queue.length === 0) return;

    // A module the customer has not contracted is shown, faded, but never sent:
    // the API refuses the whole batch over one such module.
    const contracted = new Set(extraction.contractedModules
      .filter((module) => module.contracted)
      .map((module) => module.module));

    stopRequested.current = false;
    setStopping(false);
    setStep("migrating");
    setOutcomes([]);
    setProgress({ ...EMPTY_PROGRESS, total: queue.length });
    setError(null);
    setBusy(true);

    for (let start = 0; start < queue.length; start += BATCH_SIZE) {
      if (stopRequested.current || !mounted.current) break;

      const batch = queue.slice(start, start + BATCH_SIZE).map((candidate) => ({
        legacyId: candidate.legacyId,
        email: candidate.email,
        modules: candidate.modules.filter((module) => contracted.has(module)),
        lockLegacyPassword: lockLegacy.has(candidate.legacyId) && !candidate.legacyLocked
      }));

      try {
        // eslint-disable-next-line no-await-in-loop
        const result = await service.provision(extraction.tenantId, batch);

        if (!mounted.current) return;

        setOutcomes((current) => [...current, ...result.outcomes]);
        setProgress((current) => ({
          ...current,
          done: current.done + result.outcomes.length,
          created: current.created + result.createdCount,
          updated: current.updated + result.updatedCount,
          skipped: current.skipped + result.skippedCount,
          errors: current.errors + result.errorCount,
          passwordsPreserved: current.passwordsPreserved + result.passwordsPreservedCount,
          resetEmailsSent: current.resetEmailsSent + result.resetEmailsSentCount,
          legacyLocked: current.legacyLocked + result.legacyLockedCount,
          legacyLockFailed: current.legacyLockFailed + result.outcomes.filter((outcome) => outcome.legacyLockReason
            && !LOCKED_REASONS.has(outcome.legacyLockReason)).length
        }));
      } catch (rejection) {
        if (!mounted.current) return;

        // A failed batch stops the loop: carrying on would pile more failures of
        // the same cause onto the customer's database.
        setError(toLegacyMigrationError(rejection));
        break;
      }
    }

    if (mounted.current) {
      setBusy(false);
      setStep("done");
    }
  }, [extraction, lockLegacy, selected, service]);

  const stop = useCallback(() => {
    stopRequested.current = true;
    setStopping(true);
  }, []);

  const leave = useCallback(() => {
    setError(null);
    setStep("customer");
    setExtraction(null);
    setLockLegacy(new Set());
    setOutcomes([]);
    setProgress(EMPTY_PROGRESS);
  }, []);

  // First load: configuration plus the tenant list.
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const [configuration, list] = await Promise.all([service.getStatus(), service.listTenants()]);

        if (cancelled || !mounted.current) return;

        setStatus(configuration);
        setTenants(list);
        setTenantsLoaded(true);
      } catch (rejection) {
        if (!cancelled && mounted.current) setError(toLegacyMigrationError(rejection));
      } finally {
        if (!cancelled && mounted.current) setLoading(false);
      }
    };

    load();

    return () => { cancelled = true; };
  }, [service]);

  return {
    step,
    status,
    tenants,
    tenantsLoaded,
    extraction,
    selected,
    lockLegacy,
    progress,
    outcomes,
    stopping,
    loading,
    busy,
    error,
    startExtraction,
    toggle,
    toggleMany,
    toggleLock,
    toggleLockMany,
    migrate,
    stop,
    leave
  };
};

export default useLegacyMigration;
